import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import { cutString, formatCurrency, roundAssetAmount, successToast, useTestMode } from '../util/Util'
import Checkbox from '../components/Checkbox'
import PageHeader from '../components/PageHeader'
import { PrimaryButton } from '../components/Button'
import _ from 'lodash'
import { FIAT_ACCOUNT_FLAGS, FiatAccountType, FiatDestinationCurrency, RADOM_COLORS, WINDOW_SIZE } from '../util/Constants'
import Table from '../components/Table'
import CreateVirtualAccountModal from '../components/CreateVirutalAccountModal'
import Radom, { BridgeVirtualAccountCapabilities, Deposit, DepositAccount, DepositAccountType, GeneralDepositMethod } from '../state/Radom'
import Spinner from '../components/Spinner'
import { Chevron } from '../icons/Chevron'
import { getMethod, getMethodLogoByTicker } from '../util/Managed'
import { SectionBreakerHorizontal, ClickableSection } from '../components/Shared'
import VirtualAccountDetailsModal from '../components/VirtualAccountDetailsModal'
import TimeText from '../components/TimeText'
import { useNavigate } from 'react-router-dom'
import LineGraph from '../components/LineGraph'
import { endOfDay, format, subDays } from 'date-fns'
import Treasury from '../state/Treasury'
import User from '../state/User'
import { Currencies } from '../util/Currencies'
import styled from 'styled-components'

const VirtualAccountsContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 30px 48px;
  flex-direction: row;
  gap: 20px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    flex-direction: column;
    padding: 40px;
  }
`

const LeftSection = styled.div`
  display: flex;
  width: 60%;
  flex-direction: column;
  gap: 20px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    width: 100%;
  }
`

const RightSection = styled.div`
  border-left: 1px solid ${RADOM_COLORS.GRAY_DARK};
  display: flex;
  width: 40%;
  flex-direction: column;
  gap: 20px;
  padding-left: 20px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    width: 100%;
    border-left: none;      
    padding-left: 0;       
    margin-top: 20px;      
  }
`

export interface FiatDepositSummary {
  in: {
    img: string | undefined
    amount: string | number
    ticker: string
  }
  out: {
    img: string | undefined
    amount: string | number
    ticker: string
  }
}

const VirtualAccounts = observer((): ReactElement => {
  const batch = 100
  const navigate = useNavigate()
  const [testMode] = useTestMode()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false)
  const [curPage, setCurPage] = useState(0)
  const [isCreateVirtualAccountModalOpen, setIsCreateVirtualAccountModalOpen] = useState(false)
  const [isVirtualAccountDetailsModalOpen, setIsVirtualAccountDetailsModalOpen] = useState(false)
  const [virtualAccounts, setVirtualAccounts] = useState<DepositAccount[]>([])
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [metricsData, setMetricsData] = useState<Array<{
    x: number
    y: number
  }>>([])
  const [totalDeposit, setTotalDeposit] = useState<number>(0)
  const [fiatAccountTypes, setFiatAccountTypes] = useState<FiatAccountType[]>([
    FiatAccountType.BRL,
    FiatAccountType.EUR,
    FiatAccountType.MXN,
    FiatAccountType.USD
  ])

  const [virtualAccountsCapabilities, setVirtualAccountsCapabilities] = useState<BridgeVirtualAccountCapabilities>({
    canOpenAch: false,
    canOpenSepa: false,
    canOpenSpei: false,
    canOpenPix: false
  })

  const [currentDetails, setCurrentDetails] =
    useState<{
      subtitle: string
      subtitleIcon: string
      items: Array<{ title: string, value: string, isCopy: boolean }>
      id: string
    }>({ subtitle: '', subtitleIcon: '', items: [], id: '' })

  const availableAccountTypes = useMemo(() => {
    const arr: Array<{ type: FiatAccountType, available: boolean }> = []
    if (virtualAccountsCapabilities.canOpenPix) {
      arr.push({ type: FiatAccountType.BRL, available: true })
    } else {
      arr.push({ type: FiatAccountType.BRL, available: false })
    }
    if (virtualAccountsCapabilities.canOpenSpei) {
      arr.push({ type: FiatAccountType.MXN, available: true })
    } else {
      arr.push({ type: FiatAccountType.MXN, available: false })
    }
    if (virtualAccountsCapabilities.canOpenSepa) {
      arr.push({ type: FiatAccountType.EUR, available: true })
    } else {
      arr.push({ type: FiatAccountType.EUR, available: false })
    }
    if (virtualAccountsCapabilities.canOpenAch) {
      arr.push({ type: FiatAccountType.USD, available: true })
    } else {
      arr.push({ type: FiatAccountType.USD, available: false })
    }
    return arr
  }, [virtualAccountsCapabilities])

  const availableCurrencies = useMemo(() => {
    const arr: FiatDestinationCurrency[] = [FiatDestinationCurrency.USDC]
    if (User.organization?.features?.isUsdtEnabled) {
      arr.push(FiatDestinationCurrency.USDT)
    }
    return arr
  }, [User.organization?.features])

  const getFiatDepositSummary = (deposit: Deposit): FiatDepositSummary => {
    const paymentMethodOut = getMethod(
      deposit.depositMethod.Fiat?.onrampNetwork || '',
      deposit.depositMethod.Fiat?.onrampToken)
    const paymentMethodIn = getMethod(
      'Fiat',
      deposit.depositMethod.Fiat?.fiatCurrency)
    return {
      in: {
        img: paymentMethodIn.logo,
        amount: deposit.fiatGrossAmount || '',
        ticker: paymentMethodIn.ticker
      },
      out: {
        img: paymentMethodOut.logo,
        amount: deposit.netAmount,
        ticker: paymentMethodOut.ticker
      }
    }
  }

  const getAccountType = (acc: DepositAccount): FiatAccountType | null => acc.accountDetails.Fiat?.accountType.spei
    ? FiatAccountType.MXN
    : acc.accountDetails.Fiat?.accountType.pix
      ? FiatAccountType.BRL
      : acc.accountDetails.Fiat?.accountType.iban
        ? FiatAccountType.EUR
        : acc.accountDetails.Fiat?.accountType.us
          ? FiatAccountType.USD
          : null

  const fetchVirtualAccounts = async (): Promise<void> => {
    setIsLoading(true)
    setVirtualAccounts([])

    Radom.listDepositAccounts(testMode, 0, batch, undefined, [DepositAccountType.Fiat])
      .then(res => {
        setVirtualAccounts(res.data)
      })
      .finally(() => setIsLoading(false)
      )
  }

  const fetchDeposits = async (): Promise<void> => {
    setIsLoadingDeposits(true)
    setDeposits([])

    Radom.listDeposits(testMode, [GeneralDepositMethod.Fiat], 0, batch)
      .then(async ({ data: firstData, totalCount }) => {
        let deps = [...firstData]

        if (totalCount > batch) {
          const totalBatches = Math.ceil(totalCount / batch)
          for (let i = 1; i < totalBatches; i++) {
            const offset = i * batch
            const { data: nextData } = await Radom.listDeposits(
              testMode,
              [GeneralDepositMethod.Fiat],
              offset,
              batch
            )
            deps = deps.concat(nextData)
          }
        }
        const total = deps.reduce((acc, curr) => acc + Number(curr.grossAmount), 0)

        const end = endOfDay(new Date())

        const data = Array(31)
          .fill(0)
          .map((_, idx) => ({
            x: subDays(end, idx).getTime(),
            y: 0
          }))
          .reverse()

        deps.forEach((d: Deposit) => {
          const eventTime = new Date(d.depositedAt).getTime()
          if (eventTime < data[0].x) return

          const fiat = d.depositMethod.Fiat?.fiatCurrency ?? User.organization?.defaultCurrency ?? 'USD'
          const conversion = Treasury.priceQuotes.find(
            (p) => p.from.toLowerCase() === fiat.toLowerCase()
          )

          for (let i = 0; i < data.length; i++) {
            if (eventTime > data[i].x) continue
            data[i].y += Number(d.fiatGrossAmount ?? 0) * (Number(conversion?.value) ?? 1)
            break
          }
        })

        setMetricsData(data)
        setDeposits(deps)
        setTotalDeposit(total)
      })
      .finally(() => {
        setIsLoadingDeposits(false)
      })
  }

  const debouncedFetchVirtualAccounts = useMemo(() => _.debounce(fetchVirtualAccounts, 300), [curPage, testMode])
  const debouncedFetchDeposits = useMemo(() => _.debounce(fetchDeposits, 300), [testMode])

  const filteredVirtualAccounts = useMemo(() => virtualAccounts.filter(item => {
    const accType = getAccountType(item)
    if (!accType) return false
    return fiatAccountTypes.includes(accType)
  }), [virtualAccounts, fiatAccountTypes])

  useEffect(() => {
    debouncedFetchVirtualAccounts()
    return () => debouncedFetchVirtualAccounts.cancel()
  }, [debouncedFetchVirtualAccounts])

  useEffect(() => {
    setCurPage(0)
    debouncedFetchDeposits()
    return () => debouncedFetchDeposits.cancel()
  }, [debouncedFetchDeposits])

  useEffect(() => {
    Radom.getVirtualAccountsCapabilities().then(res => setVirtualAccountsCapabilities(res)).catch(() => {})
  }, [])

  if (testMode) {
    return <VirtualAccountsContainer>
      <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 24 }}>Virtual Accounts are not available for using testnet</span>
      </div>
    </VirtualAccountsContainer>
  }

  return <VirtualAccountsContainer>
    <LeftSection>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginRight: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <PageHeader title='Virtual Accounts' subTitle='View your virtual accounts' />
          <div style={{ display: 'flex', flexDirection: 'row', gap: 20 }}>
            <Dropdown
              selectedContent={<div style={{ fontSize: 14 }}>{'Account Type'}</div>}
              dropdownContent={<div>
                {Object.values(FiatAccountType).map(s =>
                  <DropdownItem
                    key={s}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}
                    onClick={() => {
                      if (isLoading) return
                      setFiatAccountTypes((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s])
                    }}>
                    <Checkbox size={16} checked={fiatAccountTypes.includes(s)} />
                    <img
                      style={{
                        width: 16,
                        maxHeight: 16,
                        maxWidth: 16
                      }}
                      src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[s]}.svg`}
                    />
                    <span>{s}</span>
                  </DropdownItem>
                )}
              </div>} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: 20, justifyContent: 'flex-end' }}>
          <PrimaryButton style={{ fontSize: 14 }} onClick={() => {
            setIsCreateVirtualAccountModalOpen(true)
          }}>
            <span>Create account</span>
          </PrimaryButton>
        </div>
      </div>
      <SectionBreakerHorizontal/>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginRight: '20px', alignItems: 'center' }}>
        {isLoading
          ? <Spinner/>
          : filteredVirtualAccounts.map((item, idx) => {
            const accountName = item.name
            const accountType = getAccountType(item)
            const sourceCurrency = item.accountDetails.Fiat?.accountType.iban?.sourceCurrency ||
              item.accountDetails.Fiat?.accountType.us?.sourceCurrency ||
              item.accountDetails.Fiat?.accountType.spei?.sourceCurrency ||
              item.accountDetails.Fiat?.accountType.pix?.sourceCurrency
            const destinationCurrency = item.accountDetails.Fiat?.accountType.iban?.destinationCurrency ||
              item.accountDetails.Fiat?.accountType.us?.destinationCurrency ||
              item.accountDetails.Fiat?.accountType.spei?.destinationCurrency ||
              item.accountDetails.Fiat?.accountType.pix?.destinationCurrency
            const inPaymentMethod = getMethod('Fiat', sourceCurrency?.toUpperCase() || accountType || 'USD')
            const outPaymentMethod = getMethodLogoByTicker(destinationCurrency?.toUpperCase() ?? 'USDC')

            const items: Array<{ title: string, value: string, isCopy: boolean }> = []

            const bankBeneficiaryName = item.accountDetails.Fiat?.accountType.us?.bankBeneficiaryName
            if (bankBeneficiaryName) {
              items.push({ title: 'Beneficiary name', value: bankBeneficiaryName, isCopy: true })
            }

            const iban = item.accountDetails.Fiat?.accountType.iban?.iban
            if (iban) {
              items.push({ title: 'IBAN', value: iban, isCopy: true })
            }

            const bic = item.accountDetails.Fiat?.accountType.iban?.bic
            if (bic) {
              items.push({ title: 'BIC', value: bic, isCopy: true })
            }

            const routingNumber = item.accountDetails.Fiat?.accountType.us?.routingNumber
            if (routingNumber) {
              items.push({ title: 'Routing number', value: routingNumber, isCopy: true })
            }

            const accountNumber = item.accountDetails.Fiat?.accountType.us?.accountNumber
            if (accountNumber) {
              items.push({ title: 'Account number', value: accountNumber, isCopy: true })
            }

            const clabe = item.accountDetails.Fiat?.accountType.spei?.clabe
            if (clabe) {
              items.push({ title: 'CLABE', value: clabe, isCopy: true })
            }

            const brCode = item.accountDetails.Fiat?.accountType.pix?.brCode
            if (brCode) {
              items.push({ title: 'BR CODE', value: brCode, isCopy: true })
            }

            const bankName = item.accountDetails.Fiat?.bankName
            if (bankName) {
              items.push({ title: 'Bank name', value: bankName, isCopy: true })
            }

            const bankAddress = item.accountDetails.Fiat?.bankAddress
            if (bankAddress) {
              items.push({ title: 'Bank address', value: bankAddress, isCopy: true })
            }

            const paymentRailsItems = (item.accountDetails.Fiat?.accountType.iban?.sourcePaymentRails ||
              item.accountDetails.Fiat?.accountType.us?.sourcePaymentRails ||
              item.accountDetails.Fiat?.accountType.spei?.sourcePaymentRails ||
              item.accountDetails.Fiat?.accountType.pix?.sourcePaymentRails)?.map(str => {
              const parts = str.split('_')
              const cleaned = parts.length === 2 ? parts[0] : str
              return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
            })

            if (paymentRailsItems) {
              items.push({ title: 'Payment rails', value: paymentRailsItems.join(', '), isCopy: false })
            }

            const destinationPaymentRail = item.accountDetails.Fiat?.accountType.iban?.destinationPaymentRail ||
              item.accountDetails.Fiat?.accountType.us?.destinationPaymentRail ||
              item.accountDetails.Fiat?.accountType.spei?.destinationPaymentRail ||
              item.accountDetails.Fiat?.accountType.pix?.destinationPaymentRail

            items.push({ title: 'Destination currency', value: destinationCurrency?.toUpperCase() ?? 'USDC', isCopy: false })

            items.push({ title: 'Destination network', value: destinationPaymentRail ? (destinationPaymentRail.charAt(0).toUpperCase() + destinationPaymentRail.slice(1).toLowerCase()) : 'Ethereum', isCopy: false })

            return <ClickableSection key={idx} style={{ width: '100%', cursor: 'pointer' }} onClick={() => {
              setCurrentDetails({
                subtitle: accountName,
                subtitleIcon: `https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[accountType || '']}.svg`,
                items,
                id: item.id
              })
              setIsVirtualAccountDetailsModalOpen(true)
            }}>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                  <img
                    style={{
                      width: 26,
                      height: 26
                    }}
                    src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[accountType || '']}.svg`}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: '14px', color: RADOM_COLORS.BLACK }}>{accountName}</span>
                    <span style={{ fontSize: '12px', color: RADOM_COLORS.RADOM_BLUE }}>{item.id}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <img src={inPaymentMethod.logo} style={{ width: 26, height: 26 }} />
                  <span style={{ fontSize: '12px', color: RADOM_COLORS.BLACK }}>{inPaymentMethod.ticker}</span>
                  <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                  <span style={{ fontSize: '12px', color: RADOM_COLORS.BLACK }}>{destinationCurrency?.toUpperCase() ?? 'USDC'}</span>
                  <img src={outPaymentMethod} style={{ width: 26, height: 26 }} />
                </div>
              </div>
            </ClickableSection>
          }
          )}
      </div>
    </LeftSection>
    <RightSection>
      <span style={{ fontSize: 18 }}>Total deposits</span>
      {isLoadingDeposits
        ? <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner/></div>
        : <span style={{ fontSize: 24, display: 'flex', flexDirection: 'row' }}><div style={{ width: 'fit-content' }}>{Currencies.find(item => item.ticker === (User.organization?.defaultCurrency ?? 'USD'))?.icon ?? '$'}</div>{totalDeposit
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        }</span>
      }
      {metricsData.filter(item => item.y !== 0).length > 0 &&
        <>
          <span style={{ fontSize: 18 }}>Deposits from last 30 days</span>
          <LineGraph
            isLoading={isLoadingDeposits}
            height={270}
            data={metricsData}
            formatXAxisLabelFn={val => {
              return format(new Date(val), 'MM/dd/yyyy')
            }}
            formatXLabelFn={val => {
              return format(val, 'MM/dd/yyyy h:mm aaa')
            }}
            formatYLabelFn={(v: number) => formatCurrency(v, User.organization?.defaultCurrency ?? 'USD')
            }
          />
        </>
      }
      <Table
        borderless
        disableOnClickState
        onClickRow={i => {
          navigate(`/deposit/${deposits[i].id}?source=virtual_accounts`)
        }}
        headers={['Account', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
        isLoading={isLoadingDeposits}
        items={deposits.slice(0, 10).map(d => {
          const summary = getFiatDepositSummary(d)
          const acc = virtualAccounts.find(acc => acc.id === d.depositAccountId)
          const name = d.depositAccountName || acc?.name || d.depositAccountId
          let icon: string | null = null
          if (acc) {
            const accountType = getAccountType(acc)
            icon = `https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[accountType || '']}.svg`
          }

          return [
            <div key={'1'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {icon && <img src={icon} style={{ width: 16, height: 16 }} />}
              <span style={{ fontSize: 12 }}>{cutString(name, 6)}</span>
            </div>,
            <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <img src={summary.in.img} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12 }}>
                  {roundAssetAmount(
                    summary.in.ticker,
                    Number(summary.in.amount)
                  )}
                </span>
              </div>
              <span style={{ fontSize: 12 }}>{summary.in.ticker}</span>
              <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <img src={summary.out.img} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12 }}>
                  {roundAssetAmount(
                    summary.out.ticker,
                    Number(summary.out.amount)
                  )}
                </span>
                <span style={{ fontSize: 12 }}>{summary.out.ticker}</span>
              </div>
            </div>,
            <div key={'3'}>
              <TimeText date={new Date(d.depositedAt)} fontSize={12} />
            </div>
          ]
        })}
        emptyPlaceholderText='No deposits yet'
      />
    </RightSection>

    {
      isCreateVirtualAccountModalOpen &&
        <CreateVirtualAccountModal
          title='Create virtual account'
          visible={isCreateVirtualAccountModalOpen}
          availableAccountTypes={availableAccountTypes}
          availableCurrencies={availableCurrencies}
          onClose={() => setIsCreateVirtualAccountModalOpen(false)}
          onAccountCreated={() => {
            setIsCreateVirtualAccountModalOpen(false)
            debouncedFetchVirtualAccounts()
          }} />
    }

    {
      isVirtualAccountDetailsModalOpen &&
        <VirtualAccountDetailsModal
          title='Account Details'
          {...currentDetails}
          visible={isVirtualAccountDetailsModalOpen}
          onClose={() => setIsVirtualAccountDetailsModalOpen(false)}
          onAccountDelete={async () => {
            setIsVirtualAccountDetailsModalOpen(false)
            setIsLoading(true)
            Radom.deleteDepositAccount(currentDetails.id)
              .then(async () => await fetchVirtualAccounts())
              .then(() => {
                successToast('Account deleted')
              })
          }}
        />
    }
  </VirtualAccountsContainer>
})

export default VirtualAccounts
